'use client';

import { createElement, useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, User as UserIcon, ChevronDown, ShoppingCart, GraduationCap, CalendarClock, ArrowRight, Home } from 'lucide-react';
import { settingsApi, catalogApi, type ExamType } from '@/lib/api';
import { Button } from './ui';
import { examIcon } from '@/lib/exam-icons';
import { NavDropdown } from './nav/NavDropdown';
import { MobileNavAccordion } from './nav/MobileNavAccordion';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useTranslations } from 'next-intl';
import { SearchButton } from './search/SearchButton';
import { SearchCombobox } from './search/SearchCombobox';
import { SearchDialog } from './search/SearchDialog';
import { useCatalogSearch } from './search/useCatalogSearch';
import { isCoursesComingSoonIn } from '@/lib/courses-gate';

/** The Program dropdown's two fixed product entries — not data-driven (there is
 *  no LiveClass model / _count to fetch). Live Class is always "Segera"; Kursus
 *  Mandiri follows the same courses.comingSoon.enabled flag as /courses, so the
 *  badge never contradicts a catalog that is already open. */
interface ProgramItem {
  key: string;
  comingSoon: boolean;
  href: string;
  icon: typeof GraduationCap;
  label: string;
  desc: string;
}

export interface NavbarProps {
  breadcrumbs?: { label: string; href: string }[];
}

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502';
// px, not a rem class: `html` is 14px here, which shrinks max-w-7xl to 1120px.
const NAV_CONTAINER = 'max-w-[1280px]';

export function Navbar({ breadcrumbs }: NavbarProps) {
  const containerClass = NAV_CONTAINER;
  const t = useTranslations('nav');
  const tHero = useTranslations('home.hero');
  const { isAuthenticated } = useAuth();
  const { itemCount, openDrawer } = useCart();
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [openDropdown, setOpenDropdown] = useState<'ujian' | 'program' | null>(null);
  const [mobileUjianOpen, setMobileUjianOpen] = useState(false);
  const [mobileProgramOpen, setMobileProgramOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [coursesGated, setCoursesGated] = useState(false);
  const search = useCatalogSearch({ examTypes, coursesGated });

  const canRegister = !isAuthenticated && registrationEnabled;
  const isUjianActive = pathname.startsWith('/tests');
  const isProgramActive = pathname.startsWith('/courses') || pathname.startsWith('/live-class');

  const programItems: ProgramItem[] = [
    {
      key: 'kursus-mandiri',
      href: '/courses',
      icon: GraduationCap,
      label: t('programKursusMandiri'),
      desc: t('programKursusMandiriDesc'),
      comingSoon: coursesGated,
    },
    {
      key: 'live-class',
      href: '/live-class',
      icon: CalendarClock,
      label: t('programLiveClass'),
      desc: t('programLiveClassDesc'),
      comingSoon: true,
    },
  ];

  const staticLinks = [
    { label: t('liveEvents'), href: '/events' },
    { label: t('harga'), href: '/harga' },
  ];

  useEffect(() => {
    settingsApi.getPublic().then((settings) => {
      setCoursesGated(isCoursesComingSoonIn(settings));
      if ('registration.enabled' in settings) {
        setRegistrationEnabled(Boolean(settings['registration.enabled']));
      }
    }).catch(() => {});

    catalogApi.getExamTypes().then(setExamTypes).catch(() => {});
  }, []);

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className={`${containerClass} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="flex items-center justify-between h-16 lg:h-[72px]">
            {/* Logo */}
            <Link href="/" className="flex items-center group shrink-0">
              <span className="text-xl lg:text-2xl font-bold text-slate-900">
                Aksa<span className="text-teal-600">demia</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 shrink-0">
              {/* Paket Ujian dropdown */}
              <NavDropdown
                id="ujian"
                label={t('paketUjian')}
                isOpen={openDropdown === 'ujian'}
                isActive={isUjianActive}
                onToggle={() => setOpenDropdown((v) => v === 'ujian' ? null : 'ujian')}
                onClose={() => setOpenDropdown(null)}
                panelWidthClassName="w-[460px] xl:w-[720px]"
              >
                <div className="flex">
                  <div className="min-w-0 flex-1 p-3">
                    <div className="grid grid-cols-2 gap-1">
                      {examTypes.filter((et) => et._count.modules > 0).map((et) => {
                        const Icon = examIcon(et.slug);
                        return (
                          <Link
                            key={et.id}
                            href={`/tests?examTypeSlug=${et.slug}`}
                            onClick={() => setOpenDropdown(null)}
                            className="group flex items-center gap-3 rounded-xl p-2.5 transition-colors hover:bg-slate-50"
                          >
                            <span
                              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-white"
                              style={{ backgroundColor: et.color ?? '#0d9488' }}
                            >
                              <Icon className="h-5 w-5" />
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-[15px] font-semibold text-slate-900 group-hover:text-teal-700">{et.name}</span>
                              <span className="block text-xs text-slate-500">{t('paketCount', { count: et._count.modules })}</span>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                    <Link
                      href="/tests"
                      onClick={() => setOpenDropdown(null)}
                      className="mt-2 flex items-center justify-between rounded-xl border-t border-slate-100 px-2.5 py-2.5 text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-50"
                    >
                      {t('lihatSemuaPaketUjian')}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>

                  {/* Free-entry panel. Only from `xl`, where the menu is wide enough to hold it. */}
                  <div className="bg-pattern hidden w-60 flex-shrink-0 flex-col justify-between gap-4 bg-teal-700 p-5 text-white xl:flex">
                    <div>
                      <p className="font-display text-lg font-bold">{tHero('freeTitle')}</p>
                      <p className="mt-1 text-sm text-teal-50">{tHero('freeDesc')}</p>
                    </div>
                    <Button
                      href={canRegister ? `${DASHBOARD_URL}/register` : '/tests'}
                      variant="primary"
                      size="md"
                      className="w-full justify-center"
                    >
                      {canRegister ? tHero('cta') : tHero('secondaryCta')}
                    </Button>
                  </div>
                </div>
              </NavDropdown>

              {/* Program dropdown — Kursus Mandiri + Live Class, both fixed
                  entries (not data-driven); "Segera" per item.comingSoon. */}
              <NavDropdown
                id="program"
                label={t('program')}
                isOpen={openDropdown === 'program'}
                isActive={isProgramActive}
                onToggle={() => setOpenDropdown((v) => v === 'program' ? null : 'program')}
                onClose={() => setOpenDropdown(null)}
              >
                <div className="p-3 space-y-1">
                  {programItems.map((item) => (
                    <Link
                      key={item.key}
                      href={item.href}
                      onClick={() => setOpenDropdown(null)}
                      className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                    >
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-teal-600 text-white">
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-900 group-hover:text-teal-700">
                          {item.label}
                          {item.comingSoon && (
                            <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                              {t('segeraBadge')}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500 mt-0.5">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </NavDropdown>

              {staticLinks.map((link) => {
                const isActive = pathname === link.href || pathname.startsWith(link.href + '/');
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`px-3 py-2 rounded-md text-[15px] font-medium transition-all inline-flex items-center gap-1.5 ${
                      isActive
                        ? 'text-teal-600 bg-teal-50'
                        : 'text-slate-600 hover:text-teal-600 hover:bg-teal-50'
                    }`}
                  >
                    {link.label}
                    {/* Attention dot, NOT a live indicator. 781e4319 made this badge
                        always-on by dropping the hasLiveNow lookup, which left a red
                        pulsing dot (universally "broadcasting now") plus an sr-only
                        "Live" on every page whether or not an event was running: a
                        claim that was false most of the time, and announced as fact
                        to screen readers. Keeping the attention draw that commit
                        wanted, minus the falsehood: brand accent instead of alarm
                        red, no pulse, decorative rather than labelled. Re-gating on
                        real status would cost a live-event fetch on every marketing
                        pageview, which is not worth it for a dot. */}
                    {link.href === '/events' && (
                      <span
                        aria-hidden="true"
                        className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500"
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-2 min-w-0">
              {/* Input from xl, where it fits; icon button (opens the dialog) at lg. shrink-[8]: the
                  input gives way first when the bar is tight, so nothing else wraps. */}
              <SearchCombobox search={search} className="hidden xl:block shrink-[8]" />
              <SearchButton onClick={() => setIsSearchOpen(true)} className="xl:hidden" />
              <button
                onClick={openDrawer}
                className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">
                    {itemCount}
                  </span>
                )}
              </button>
              {isAuthenticated ? (
                <Button href={DASHBOARD_URL} variant="secondary" size="md">
                  <UserIcon className="w-4 h-4" />
                  {t('dashboard')}
                </Button>
              ) : (
                <>
                  <Button href={`${DASHBOARD_URL}/login`} variant="secondary" size="md">
                    {t('masuk')}
                  </Button>
                  {registrationEnabled && (
                    <Button href={`${DASHBOARD_URL}/register`} variant="primary" size="md">
                      {t('mulaiGratis')}
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Mobile: search + menu buttons */}
            <div className="flex items-center gap-1 lg:hidden">
              <SearchButton onClick={() => setIsSearchOpen(true)} />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <SearchDialog
        open={isSearchOpen}
        onOpen={() => setIsSearchOpen(true)}
        onClose={() => setIsSearchOpen(false)}
        search={search}
      />

      {/* Breadcrumbs — deliberately OUTSIDE the sticky <nav> above so it scrolls away
          with the page instead of adding its height to the sticky header. */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <div className="border-b border-slate-200 bg-white">
          <div className={`${containerClass} mx-auto px-4 sm:px-6 lg:px-8 py-2`}>
            <nav className="flex items-center gap-2 text-sm">
              <Link href="/" className="text-slate-500 hover:text-teal-600 flex items-center gap-1">
                <Home className="w-3 h-3" />
                {t('beranda')}
              </Link>
              {breadcrumbs.map((crumb, index) => (
                <span key={crumb.href} className="flex items-center gap-2 min-w-0">
                  <ChevronDown className="w-3 h-3 text-slate-400 -rotate-90" />
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-slate-900 font-medium truncate max-w-[200px] min-w-0">{crumb.label}</span>
                  ) : (
                    <Link href={crumb.href} className="text-slate-500 hover:text-teal-600">
                      {crumb.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[65] bg-slate-900/20 backdrop-blur-sm transition-opacity lg:hidden ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-72 max-w-full bg-white z-[70] shadow-xl transform transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-5 overflow-y-auto h-full">
          <div className="flex items-center justify-between mb-6">
            <Link href="/" className="flex items-center">
              <span className="text-lg font-bold text-slate-900">
                Aksa<span className="text-teal-600">demia</span>
              </span>
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-1">
            {/* Paket Ujian — collapsible */}
            <MobileNavAccordion
              id="ujian"
              label={t('paketUjian')}
              isOpen={mobileUjianOpen}
              isActive={isUjianActive}
              onToggle={() => setMobileUjianOpen((v) => !v)}
            >
              {examTypes.filter((et) => et._count.modules > 0).map((et) => (
                <Link
                  key={et.id}
                  href={`/tests?examTypeSlug=${et.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
                    style={{ backgroundColor: et.color ?? '#0d9488' }}
                  >
                    {createElement(examIcon(et.slug), { className: 'w-4 h-4' })}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 group-hover:text-teal-700 truncate">{et.name}</p>
                    <p className="text-[11px] text-slate-400">{t('paketCount', { count: et._count.modules })}</p>
                  </div>
                </Link>
              ))}
              <Link
                href="/tests"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-teal-600 hover:bg-teal-50 transition-colors"
              >
                {t('lihatSemuaPaketUjian')}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </MobileNavAccordion>

            {/* Program — collapsible, Kursus Mandiri + Live Class */}
            <MobileNavAccordion
              id="program"
              label={t('program')}
              isOpen={mobileProgramOpen}
              isActive={isProgramActive}
              onToggle={() => setMobileProgramOpen((v) => !v)}
            >
              {programItems.map((item) => (
                <Link
                  key={item.key}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-50">
                    <item.icon className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800 group-hover:text-teal-700">
                      {item.label}
                      {item.comingSoon && (
                        <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                          {t('segeraBadge')}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{item.desc}</p>
                  </div>
                </Link>
              ))}
            </MobileNavAccordion>

            {staticLinks.map((link) => {
              const isActive = pathname === link.href || pathname.startsWith(link.href + '/');
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium ${
                    isActive
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-slate-700 hover:bg-teal-50 hover:text-teal-600'
                  }`}
                >
                  {link.label}
                  {/* Decorative attention dot, not a live indicator. See the
                      desktop variant above for why. */}
                  {link.href === '/events' && (
                    <span
                      aria-hidden="true"
                      className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500"
                    />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 space-y-2">
            {itemCount > 0 && (
              <button
                onClick={() => { setIsMobileMenuOpen(false); openDrawer(); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-teal-600 bg-teal-50 font-semibold text-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                {t('keranjang', { count: itemCount })}
              </button>
            )}
            {isAuthenticated ? (
              <Button href={DASHBOARD_URL} variant="secondary" size="md" className="w-full justify-center">
                <UserIcon className="w-4 h-4" />
                {t('dashboard')}
              </Button>
            ) : (
              <>
                <Button href={`${DASHBOARD_URL}/login`} variant="secondary" size="md" className="w-full justify-center">
                  {t('masuk')}
                </Button>
                {registrationEnabled && (
                  <Button href={`${DASHBOARD_URL}/register`} variant="primary" size="md" className="w-full justify-center">
                    {t('mulaiGratis')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
